/**
 * Move tool handler
 * - Drag selected objects
 * - Arrow keys: nudge by grid size (if snap is on) or 0.1m
 * - Snap to grid during drag if enabled
 * - Updates stage-store, pushes history on release
 */

import { Vec2, snapToGrid } from '@/canvas/coordinates';
import { ToolHandler, ToolContext } from './tool-handler';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';
import { useHistoryStore } from '@/core/stores/history-store';

interface MoveToolState {
  isDragging: boolean;
  dragStart: Vec2 | null;
  originalPositions: Map<string, { x: number; y: number }> | null;
}

const state: MoveToolState = {
  isDragging: false,
  dragStart: null,
  originalPositions: null,
};

const NUDGE_DISTANCE = 0.1; // meters

export function getMoveToolHandler(): ToolHandler {
  return {
    onMouseDown(ctx: ToolContext) {
      if (ctx.button !== 0) return; // Only left-click

      const { selectedObjectIds } = useEditorStore.getState();

      if (selectedObjectIds.length === 0) return;

      state.isDragging = true;
      state.dragStart = ctx.worldPos;
      state.originalPositions = new Map(
        selectedObjectIds.map((id) => [id, { x: 0, y: 0 }])
      );

      // Store original positions and push pre-drag state to history
      const stageState = useStageStore.getState();
      const { layout } = stageState;
      if (layout) {
        // Push pre-mutation snapshot BEFORE any dragging mutates the state
        useHistoryStore.getState().pushState(layout);

        for (const id of selectedObjectIds) {
          const obj = layout.objects.find((o) => o.id === id);
          if (obj && state.originalPositions) {
            state.originalPositions.set(id, { x: obj.position.x, y: obj.position.y });
          }
        }
      }
    },

    onMouseMove(ctx: ToolContext) {
      if (!state.isDragging || !state.dragStart || !state.originalPositions) return;

      const { snapToGrid: shouldSnap, gridSize, selectedObjectIds } = useEditorStore.getState();
      const stageState = useStageStore.getState();

      const delta = {
        x: ctx.worldPos.x - state.dragStart.x,
        y: ctx.worldPos.y - state.dragStart.y,
      };

      // Update each selected object
      for (const id of selectedObjectIds) {
        const original = state.originalPositions.get(id);
        if (!original) continue;

        let newX = original.x + delta.x;
        let newY = original.y + delta.y;

        // Snap to grid if enabled
        if (shouldSnap) {
          const snapped = snapToGrid({ x: newX, y: newY }, gridSize);
          newX = snapped.x;
          newY = snapped.y;
        }

        stageState.updateObject(id, { position: { x: newX, y: newY, z: 0 } });
      }
    },

    onMouseUp() {
      if (!state.isDragging) return;

      // History was already pushed in onMouseDown (pre-drag snapshot)
      useEditorStore.getState().setDirty(true);

      state.isDragging = false;
      state.dragStart = null;
      state.originalPositions = null;
    },

    onKeyDown(key: string, _ctx: Partial<ToolContext>) {
      const { selectedObjectIds, gridSize, snapToGrid: shouldSnap } = useEditorStore.getState();
      const stageState = useStageStore.getState();
      const historyState = useHistoryStore.getState();

      if (selectedObjectIds.length === 0) return;

      const nudge = shouldSnap ? gridSize : NUDGE_DISTANCE;
      let deltaX = 0;
      let deltaY = 0;

      switch (key) {
        case 'ArrowUp':
          deltaY = -nudge;
          break;
        case 'ArrowDown':
          deltaY = nudge;
          break;
        case 'ArrowLeft':
          deltaX = -nudge;
          break;
        case 'ArrowRight':
          deltaX = nudge;
          break;
        default:
          return;
      }

      // Push pre-mutation state to history BEFORE mutating
      if (stageState.layout) {
        historyState.pushState(stageState.layout);
      }

      // Move each selected object
      for (const id of selectedObjectIds) {
        const { layout } = stageState;
        if (!layout) continue;

        const obj = layout.objects.find((o) => o.id === id);
        if (!obj) continue;

        stageState.updateObject(id, {
          position: {
            x: obj.position.x + deltaX,
            y: obj.position.y + deltaY,
            z: obj.position.z,
          },
        });
      }

      useEditorStore.getState().setDirty(true);
    },

    getCursor() {
      const { selectedObjectIds } = useEditorStore.getState();
      return selectedObjectIds.length > 0 ? 'move' : 'default';
    },
  };
}
